import {useApp} from "../app.context";
import {FieldConstraint} from "../models/attributeManifest.model";
import {describeConstraint} from "../utils/clamp";

// Last-resort label from a field key, e.g. "unit.properties.movementSpeed" →
// "Movement Speed". Only used when neither the callsite nor the manifest supplies
// one, so a collapsed callsite never shows a blank label.
export function humanizeField(field?: string): string {
    if (!field) return '';
    const seg = field.split('.').pop() ?? '';
    return seg.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/^./, c => c.toUpperCase());
}

// Resolves a labelled input's presentation from the Client model's [FieldDoc]
// (label + tooltip) and [ConstraintRange], keyed by manifest `field` path. Shared
// by NumberInputLabel / SelectableLabel / BooleanInputLabel so any labelled field
// can source its label + tooltip from the model. An explicit callsite title/tooltip
// always wins; the numeric range (if any) is merged into the tooltip.
export function useFieldDoc(opts: {
    field?: string;
    title?: string;
    tooltip?: string;
    constraint?: FieldConstraint | null;
}) {
    const {getFieldConstraint} = useApp();
    const constraint = opts.constraint ?? (opts.field ? getFieldConstraint(opts.field) : undefined);

    // Dev-only coverage nudge: a field key with no manifest entry is a typo or a
    // not-yet-annotated model field (harmless — just falls back to the humanized key).
    if (process.env.NODE_ENV !== 'production' && opts.field && !constraint) {
        // eslint-disable-next-line no-console
        console.warn(`useFieldDoc: no manifest entry for field "${opts.field}" (no model label/tooltip).`);
    }

    const label = opts.title ?? constraint?.Label ?? humanizeField(opts.field);
    const tooltipText = opts.tooltip ?? constraint?.Comment ?? undefined;
    const constraintText = describeConstraint(constraint);
    const tip = (tooltipText || constraintText)
        ? <>
            {tooltipText && <span>{tooltipText}</span>}
            {constraintText &&
                <span style={{display: 'block', opacity: 0.85, marginTop: tooltipText ? 4 : 0}}>{constraintText}</span>}
          </>
        : undefined;

    return {constraint, label, tip};
}
